import Axios from "axios";

import { getAnnotationLink } from "../util/annotationLinks";
import { removeUndefinedFields } from "../util/removeUndefinedFields";

import type { TtsRequestParameters } from "./duolingoTts";

export interface VisualizationResponse {
  amplitudes: number[][];
  audio_duration: number;
  annotation: Annotation;
}

export interface Annotation {
  model_version: string;
  spans: Span[];
  missing: { text: string }[];
  audio_hash: string;
  request_parameters: TtsRequestParameters[];
  phoneme_spans?: PhonemeSpan[];
}

export interface Span {
  text: string;
  end_time: number;
  start_time: number;
  score: number;
  pronunciations: string[];
}

export interface PhonemeSpan {
  phoneme: string;
  start_time: number;
  end_time: number;
  score: number;
  viseme: number;
}

export interface AnnotationVersionResponseItem {
  key: string;
  last_modified: number;
  version: number | "generated";
}

const client = Axios.create({
  baseURL: `${process.env.REACT_APP_TRANSCRIPTION_BACKEND_ORIGIN}`,
  withCredentials: true,
});

export const getVisualizationData = async (
  ttsHash: string,
  version?: number | "generated",
): Promise<VisualizationResponse> =>
  (
    await client.get<VisualizationResponse>("/visualization", {
      params: removeUndefinedFields({
        tts_hash: ttsHash,
        version,
      }),
    })
  ).data;

export const updateAnnotationData = async (
  ttsHash: string,
  annotations: {
    spans?: Span[];
    phoneme_spans?: PhonemeSpan[];
  },
  {
    baseVersion,
    onError,
    onSuccess,
  }: {
    baseVersion?: number | "generated";
    onError?: () => void;
    onSuccess?: () => void;
  },
) => {
  await client
    .post(
      "/annotations",
      removeUndefinedFields({
        tts_hash: ttsHash,
        ...annotations,
        base_version: baseVersion,
      }),
    )
    .then(() => onSuccess?.())
    .catch(() => onError?.());
};

export const getPreviousVersions = async (
  ttsHash: string,
): Promise<AnnotationVersionResponseItem[]> =>
  (
    await client.get<AnnotationVersionResponseItem[]>("/annotations", {
      params: {
        tts_hash: ttsHash,
      },
    })
  ).data;

export const hasSpeechAnnotations = async (ttsHash: string) => {
  const cloudfrontResponse = await Axios.head(
    getAnnotationLink(ttsHash),
    { validateStatus: () => true }, // Prevent exception on no annotations
  );
  return cloudfrontResponse.status >= 200 && cloudfrontResponse.status < 300;
};

let visemeMappingMemo: Record<string, string | undefined>;
export const getVisemeMapping = async () => {
  if (!visemeMappingMemo) {
    visemeMappingMemo = (
      await client.get<Record<string, string | undefined>>("/viseme-map")
    ).data;
  }
  return visemeMappingMemo;
};
